import { useTranslations } from 'next-intl'
import { getTranslations } from 'next-intl/server'

import {
  AdditionalOption,
  BasicOption,
  OldAdditionalOption,
  ParkingType,
  PropertyType,
} from './room'
import { RoomIconName } from '../ui/room-icon/icon'

export const usePropertyTypeMap = () => {
  const t = useTranslations('common')

  return { PROPERTY_TYPE_MAP: buildPropertyTypeMap(t) }
}

export const getPropertyTypeMap = async () => {
  const t = await getTranslations('common')

  return { PROPERTY_TYPE_MAP: buildPropertyTypeMap(t) }
}

const buildPropertyTypeMap = (
  t: ReturnType<typeof useTranslations<'common'>>,
) => ({
  [PropertyType.OFFICETEL]: t('property_type.officetel'),
  [PropertyType.APARTMENT]: t('property_type.apartment'),
  [PropertyType.STUDIO]: t('property_type.studio_building'),
  [PropertyType.VILLA]: t('property_type.low_rise_apt'),
  [PropertyType.DETACHED]: t('property_type.detached_house'),
  [PropertyType.MIXED_USE]: t('property_type.mixed_use'),
  [PropertyType.GOSIWON]: t('property_type.goshiwon'),
  [PropertyType.HOTEL]: t('property_type.hotel'),
  [PropertyType.MOTEL]: t('property_type.motel_inn'),
  [PropertyType.VACATION_HOME]: t('property_type.vacation_rental'),
  [PropertyType.GUEST_HOUSE]: t('property_type.guest_house'),
  [PropertyType.SHARE_HOUSE]: t('property_type.share_house'),
})

export const useParkingTypeMap = () => {
  const t = useTranslations('room_detail')

  return { PARKING_TYPE_MAP: buildParkingTypeMap(t) }
}

const buildParkingTypeMap = (
  t: ReturnType<typeof useTranslations<'room_detail'>>,
) => ({
  [ParkingType.FREE_LEEWAY]: t('parking.type.free_leeway'),
  [ParkingType.FREE_FIRST_COME]: t('parking.type.free_first_come'),
  [ParkingType.PAID]: t('parking.type.paid_parking'),
  [ParkingType.NONE]: t('parking.type.not_available'),
})

export const useOptionMap = () => {
  const t = useTranslations('common')

  const BASIC_OPTION_MAP = buildBasicOptionMap(t)
  const ADDITIONAL_OPTION_MAP = buildAdditionalOptionMap(t)
  const OLD_ADDITIONAL_OPTION_MAP = buildOldAdditionalOptionMap(t)
  const OPTION_MAP = {
    ...BASIC_OPTION_MAP,
    ...ADDITIONAL_OPTION_MAP,
    ...OLD_ADDITIONAL_OPTION_MAP,
  }

  return {
    BASIC_OPTION_MAP,
    ADDITIONAL_OPTION_MAP,
    OLD_ADDITIONAL_OPTION_MAP,
    OPTION_MAP,
  }
}

const buildBasicOptionMap = (
  t: ReturnType<typeof useTranslations<'common'>>,
): Record<BasicOption, { label: string; icon: RoomIconName }> => ({
  [BasicOption.REFRIGERATOR]: {
    label: t('basic_option.refrigerator'),
    icon: 'refrigerator',
  },
  [BasicOption.WASHING_MACHINE]: {
    label: t('basic_option.washer'),
    icon: 'washingMachine',
  },
  [BasicOption.AIR_CONDITIONER]: {
    label: t('basic_option.air_conditioner'),
    icon: 'airConditioner',
  },
  [BasicOption.TV]: {
    label: t('basic_option.tv'),
    icon: 'tv',
  },
  [BasicOption.WIFI]: {
    label: t('basic_option.wifi'),
    icon: 'wifi',
  },
  [BasicOption.SINK]: {
    label: t('basic_option.sink'),
    icon: 'sink',
  },
  [BasicOption.GAS_STOVE]: {
    label: t('basic_option.gas_range'),
    icon: 'gasStove',
  },
  [BasicOption.INDUCTION]: {
    label: t('basic_option.induction'),
    icon: 'induction',
  },
  [BasicOption.BED]: {
    label: t('basic_option.bed'),
    icon: 'bed',
  },
})

const buildAdditionalOptionMap = (
  t: ReturnType<typeof useTranslations<'common'>>,
): Record<AdditionalOption, { label: string; icon: RoomIconName }> => ({
  [AdditionalOption.DOOR_LOCK]: {
    label: t('additional_option.door_lock'),
    icon: 'doorlock',
  },
  [AdditionalOption.CCTV]: {
    label: t('additional_option.cctv'),
    icon: 'cctv',
  },
  [AdditionalOption.MANAGEMENT_OFFICE]: {
    label: t('additional_option.management_office'),
    icon: 'managementOffice',
  },
  [AdditionalOption.DINING_TABLE]: {
    label: t('additional_option.dining_table'),
    icon: 'diningTable',
  },
  [AdditionalOption.MICROWAVE]: {
    label: t('additional_option.electric_stove'),
    icon: 'microwave',
  },
  [AdditionalOption.RICE_COOKER]: {
    label: t('additional_option.electric_rice_cooker'),
    icon: 'riceCooker',
  },
  [AdditionalOption.WATER_PURIFIER]: {
    label: t('additional_option.water_purifier'),
    icon: 'waterPurifier',
  },
  [AdditionalOption.SOFA]: {
    label: t('additional_option.sofa'),
    icon: 'sofa',
  },
  [AdditionalOption.DESK]: {
    label: t('additional_option.desk'),
    icon: 'desk',
  },
  [AdditionalOption.CLOSET]: {
    label: t('additional_option.closet'),
    icon: 'closet',
  },
  [AdditionalOption.SHOE_RACK]: {
    label: t('additional_option.shoe_rack'),
    icon: 'shoeRack',
  },
  [AdditionalOption.VANITY]: {
    label: t('additional_option.dressing_table'),
    icon: 'vanity',
  },
  [AdditionalOption.BATHTUB]: {
    label: t('additional_option.bathtub'),
    icon: 'bathtub',
  },
  [AdditionalOption.BIDET]: {
    label: t('additional_option.bidet'),
    icon: 'bidet',
  },
  [AdditionalOption.DRYER]: {
    label: t('additional_option.dryer'),
    icon: 'dryer',
  },
  [AdditionalOption.BALCONY]: {
    label: t('additional_option.balcony'),
    icon: 'balcony',
  },
  [AdditionalOption.DRESSING_ROOM]: {
    label: t('additional_option.dress_room'),
    icon: 'dressingRoom',
  },
  [AdditionalOption.CABLE_TV]: {
    label: t('additional_option.cable_tv'),
    icon: 'cableTV',
  },
  [AdditionalOption.CURTAINS]: {
    label: t('additional_option.curtain'),
    icon: 'curtain',
  },
  [AdditionalOption.AIR_PURIFIER]: {
    label: t('additional_option.air_purifier'),
    icon: 'airPurifier',
  },
  [AdditionalOption.PET_ALLOWED]: {
    label: t('popular_option.pet'),
    icon: 'petAllowed',
  },
})

const buildOldAdditionalOptionMap = (
  t: ReturnType<typeof useTranslations<'common'>>,
): Record<OldAdditionalOption, { label: string; icon?: RoomIconName }> => ({
  ...buildAdditionalOptionMap(t),
  [OldAdditionalOption.COOLING_HEATING_SYSTEM]: {
    label: '냉난방기',
  },
  [OldAdditionalOption.HEATER]: {
    label: '히터',
  },
  [OldAdditionalOption.IRON]: {
    label: '다리미',
  },
  [OldAdditionalOption.ELECTRIC_KETTLE]: {
    label: '전기포트',
  },
  [OldAdditionalOption.TABLEWARE]: {
    label: '식기류',
  },
  [OldAdditionalOption.COOKWARE]: {
    label: '조리도구',
  },
  [OldAdditionalOption.HAIR_DRYER]: {
    label: '드라이기',
  },
})
